import { DISCUSSED_TAG_ID } from './canonicalDiscussedTag'
import { PINNED_TAG_ID } from './canonicalPinnedTag'
import type { TagDefinition, TagRegistry } from './tagRegistry'

function stableId(seed: string): string {
  let hash = 2166136261
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 16777619) >>> 0
  }
  return `tag_${hash.toString(36)}`
}

export type RatingDimension = 'love' | 'readiness'

export const RATING_SCALE = 5 as const

export const LOVE_ROOT_TAG_ID = stableId('manifesto.rating.love.root')
export const READINESS_ROOT_TAG_ID = stableId('manifesto.rating.readiness.root')

interface RatingFamily {
  dimension: RatingDimension
  rootId: string
  rootName: string
  rootOrder: number
  rootColor: string
  childColors: readonly [string, string, string, string, string]
}

const RATING_FAMILIES: readonly RatingFamily[] = [
  {
    dimension: 'love',
    rootId: LOVE_ROOT_TAG_ID,
    rootName: 'שביעות רצון',
    rootOrder: 20,
    rootColor: '#f43f5e',
    childColors: ['#ffe4e6', '#fecdd3', '#fda4af', '#fb7185', '#f43f5e'],
  },
  {
    dimension: 'readiness',
    rootId: READINESS_ROOT_TAG_ID,
    rootName: 'מוכנות',
    rootOrder: 21,
    rootColor: '#8b5cf6',
    childColors: ['#ede9fe', '#ddd6fe', '#c4b5fd', '#a78bfa', '#8b5cf6'],
  },
]

export function ratingChildTagId(dimension: RatingDimension, ordinal: number): string {
  return stableId(`manifesto.rating.${dimension}.${ordinal}`)
}

function buildRatingDefaults(): Record<string, TagDefinition> {
  const out: Record<string, TagDefinition> = {}
  for (const family of RATING_FAMILIES) {
    out[family.rootId] = {
      name: family.rootName,
      parentIds: [],
      color: family.rootColor,
      order: family.rootOrder,
      kind: 'rating',
      dimension: family.dimension,
    }
    for (let ordinal = 1; ordinal <= RATING_SCALE; ordinal++) {
      const id = ratingChildTagId(family.dimension, ordinal)
      out[id] = {
        name: `${family.rootName} ${ordinal}`,
        parentIds: [family.rootId],
        color: family.childColors[ordinal - 1],
        order: ordinal,
        kind: 'rating',
        dimension: family.dimension,
        ordinal,
      }
    }
  }
  return out
}

const RATING_DEFAULTS: Readonly<Record<string, TagDefinition>> = buildRatingDefaults()

export function ensureProtectedRatingTags(registry: TagRegistry): TagRegistry {
  const tags = { ...registry.tags }
  let changed = false
  for (const [id, canonical] of Object.entries(RATING_DEFAULTS)) {
    const existing = tags[id]
    if (!existing) {
      tags[id] = { ...canonical }
      changed = true
      continue
    }
    // Protected fields: always reflect the canonical definition so colors/kind/dimension/ordinal
    // stay in sync when we tune the palette. Preserve user-toggleable flags like `hidden`.
    const next: TagDefinition = {
      ...canonical,
      ...(existing.hidden ? { hidden: true } : {}),
    }
    if (JSON.stringify(next) !== JSON.stringify(existing)) {
      tags[id] = next
      changed = true
    }
  }
  return changed ? { ...registry, tags } : registry
}

export type SectionRatings = Partial<Record<RatingDimension, number>>

/**
 * Split a flat tag-id list into category tags (for chip display), resolved
 * per-dimension rating values (for dot-meter display), and the pinned flag
 * (rendered as a pin icon, never as a category chip/dot).
 */
export function splitSectionTags(
  tagIds: readonly string[],
  registryTags: Record<string, TagDefinition>,
): { categoryTagIds: string[]; ratings: SectionRatings; pinned: boolean } {
  const categoryTagIds: string[] = []
  const ratings: SectionRatings = {}
  let pinned = false
  for (const tagId of tagIds) {
    if (tagId === PINNED_TAG_ID) {
      pinned = true
      continue
    }
    if (tagId === DISCUSSED_TAG_ID) {
      continue
    }
    const def = registryTags[tagId]
    if (def?.kind === 'rating' && def.dimension && typeof def.ordinal === 'number') {
      ratings[def.dimension as RatingDimension] = def.ordinal
      continue
    }
    // Rating root tags carry kind='rating' but no ordinal — drop them from chips,
    // they're family headers, not user-settable values.
    if (def?.kind === 'rating') {
      continue
    }
    categoryTagIds.push(tagId)
  }
  return { categoryTagIds, ratings, pinned }
}
